/**
 * Registro dos codecs de URL do app-core (c, z, g, t, q, v). Importar este
 * módulo registra os codecs (efeito colateral), como pede o contrato.
 */
import { DEFAULT_PRODUCT_ID } from '../config/products';
import { shallowArrayEqual, subscribeSelector } from '../lib/store';
import { DEFAULT_LOOP_SETTINGS, DWELL_OPTIONS_MS, FPS_MAX, FPS_MIN, useLoopStore } from '../loop/store';
import {
  decodeCenter,
  decodeLayers,
  decodeQuality,
  decodeSpeed,
  decodeTime,
  decodeZoom,
  encodeCenter,
  encodeLayers,
  encodeQuality,
  encodeSpeed,
  encodeTime,
  encodeZoom,
} from './codecFormats';
import { newLayerUid, useGoesStore } from './goes';
import { useTimeStore } from './time';
import { registerUrlCodec } from './urlCodec';
import { useViewStore } from './view';

registerUrlCodec({
  key: 'c',
  encode: () => {
    const { center, isHome } = useViewStore.getState();
    return center && !isHome ? encodeCenter(center) : null;
  },
  decode: (value) => {
    const center = decodeCenter(value);
    if (center) useViewStore.setState({ initialCenter: center });
  },
  subscribe: (onChange) => subscribeSelector(useViewStore, (s) => [s.center?.[0], s.center?.[1], s.isHome], onChange, shallowArrayEqual),
});

registerUrlCodec({
  key: 'z',
  encode: () => {
    const { zoom, isHome } = useViewStore.getState();
    return zoom !== null && !isHome ? encodeZoom(zoom) : null;
  },
  decode: (value) => {
    const zoom = decodeZoom(value);
    if (zoom !== null) useViewStore.setState({ initialZoom: zoom });
  },
  subscribe: (onChange) => subscribeSelector(useViewStore, (s) => [s.zoom, s.isHome], onChange, shallowArrayEqual),
});

registerUrlCodec({
  key: 'g',
  encode: () => {
    const { layers } = useGoesStore.getState();
    const isDefault =
      layers.length === 1 && layers[0].productId === DEFAULT_PRODUCT_ID && layers[0].opacity === 1 && layers[0].visible;
    return isDefault ? null : encodeLayers(layers);
  },
  decode: (value) => {
    const specs = decodeLayers(value);
    if (!specs) return;
    useGoesStore.getState().setLayers(specs.map((s) => ({ uid: newLayerUid(), ...s })));
  },
  subscribe: (onChange) => useGoesStore.subscribe(onChange),
});

/** Quadro a registrar no link: só com o loop parado fora do último quadro. */
function frameForUrl(): number | null {
  const { frames, index } = useTimeStore.getState();
  if (useLoopStore.getState().playing || frames.length === 0 || index >= frames.length - 1) return null;
  return frames[index];
}

registerUrlCodec({
  key: 't',
  encode: () => {
    const { period, custom, step } = useLoopStore.getState();
    const frameTime = frameForUrl();
    if (period === DEFAULT_LOOP_SETTINGS.period && step === DEFAULT_LOOP_SETTINGS.step && frameTime === null) return null;
    return encodeTime({ period, custom, step, frameTime });
  },
  // Preferência salva: período e passo, nunca o instante — ao abrir o portal
  // mostra sempre a medição mais recente. O período personalizado também é um
  // instante (uma janela de datas que em dois dias sai das 48 h): fica o
  // padrão, com o passo escolhido.
  persist: () => {
    const { period, step } = useLoopStore.getState();
    const p = period === 'custom' ? DEFAULT_LOOP_SETTINGS.period : period;
    if (p === DEFAULT_LOOP_SETTINGS.period && step === DEFAULT_LOOP_SETTINGS.step) return null;
    return encodeTime({ period: p, custom: null, step, frameTime: null });
  },
  decode: (value) => {
    const spec = decodeTime(value);
    if (!spec) return;
    useLoopStore.setState({
      period: spec.period,
      custom: spec.custom,
      step: spec.step,
      pendingFrameTime: spec.frameTime,
    });
  },
  subscribe: (onChange) => {
    const a = subscribeSelector(
      useLoopStore,
      (s) => [s.period, s.custom?.start, s.custom?.end, s.step, s.playing],
      onChange,
      shallowArrayEqual,
    );
    const b = subscribeSelector(useTimeStore, (s) => s.index, onChange);
    return () => {
      a();
      b();
    };
  },
});

registerUrlCodec({
  key: 'q',
  encode: () => encodeQuality(useLoopStore.getState().quality),
  decode: (value) => {
    const q = decodeQuality(value);
    if (q !== null) useLoopStore.setState({ quality: q });
  },
  subscribe: (onChange) => subscribeSelector(useLoopStore, (s) => s.quality, onChange),
});

registerUrlCodec({
  key: 'v',
  encode: () => {
    const { fps, mode, dwellMs } = useLoopStore.getState();
    return encodeSpeed({ fps, mode, dwellMs }, DEFAULT_LOOP_SETTINGS);
  },
  decode: (value) => {
    const spec = decodeSpeed(value, { minFps: FPS_MIN, maxFps: FPS_MAX, dwellOptionsMs: DWELL_OPTIONS_MS });
    if (spec) useLoopStore.setState(spec);
  },
  subscribe: (onChange) =>
    subscribeSelector(useLoopStore, (s) => [s.fps, s.mode, s.dwellMs], onChange, shallowArrayEqual),
});
